// Module-scope gate that holds a full-document reload while a guarded flow is active.
//
// Why this exists. Two owners can fire a full document load while the user is mid-flow:
// useBuildDetector's version-skew reload, and useCheckVersion's. useBuildDetector already
// tries to defer, but it gates on `pathsTriggerReloadLater` -- a two-entry *pathname*
// allowlist standing in for "a guarded flow is active". That proxy diverges from the thing
// it represents: usePreventBrowserBackButton has six consumers and the allowlist names the
// pathnames of two. useCheckVersion has no gate at all. This module holds the real signal,
// owned by the guard, so both owners consult the same fact.
//
// Deliberately not a hook and deliberately module scope: the guard's popstate listener is
// itself at module scope and must reach this without a React context.
//
// Deliberately NOT routed through this gate, because holding them makes things worse
// rather than better:
//   - helpers/maintenance.js  -- a safety exit; holding it traps the user in a broken app.
//   - common/ErrorBoundary    -- crash recovery; holding it leaves a dead screen.
//   - utils/func.js replaceUrlWithReload -- an *intentional* reload, issued from
//     RegisterPage (itself a guard consumer) and from
//     features/referral/HOCs/withCheckReferralLinkExpired; holding it breaks
//     restart-registration.
//   - AddTransactionPage/ver2 and FxAddTransactionPage in-page recovery reloads -- they
//     fire *on* a guarded page, so gating them would self-suppress and hang the page
//     instead of recovering it.
//   - helpers/sw.js checkValidServiceWorker -- its reload (sw.js:139) is reachable only
//     from sw.js:50, inside the `sw.isLocalhost()` branch at sw.js:48; the non-localhost
//     else at :69 registers instead. So it fires only when the page's hostname is
//     localhost, [::1], or 127.0.0.0/8 -- never on a deployed host.
//
// One further window.location.reload() exists at pages/AddTransactionPage/index.jsx (the
// pre-ver2 page). It is not routed -- src/router/index.js imports pages/AddTransactionPage/ver2
// -- so it is dead code rather than an ungated live owner.

let activeCount = 0;
let pendingReload = null;
let releaseListenersAttached = false;

const RELEASE_EVENTS = ['mousedown', 'touchstart'];

const canUseDom = () => typeof window !== 'undefined';

const detachReleaseListeners = () => {
  if (!releaseListenersAttached || !canUseDom()) return;
  RELEASE_EVENTS.forEach((name) =>
    window.removeEventListener(name, handleUserInteract, true)
  );
  releaseListenersAttached = false;
};

const attachReleaseListeners = () => {
  if (releaseListenersAttached || !canUseDom()) return;
  RELEASE_EVENTS.forEach((name) =>
    window.addEventListener(name, handleUserInteract, true)
  );
  releaseListenersAttached = true;
};

function handleUserInteract() {
  // Re-check the count: a new guarded page may have mounted between the release and this
  // interaction, in which case the reload stays held.
  if (activeCount > 0) return;

  const reload = pendingReload;
  if (!reload) {
    detachReleaseListeners();
    return;
  }

  pendingReload = null;
  detachReleaseListeners();
  reload();
}

// Called by usePreventBrowserBackButton on mount. A counter rather than a boolean: two
// consumers could in principle overlap during a transition, and a boolean would let the
// first unmount clear a flag the second still needs.
export const acquireReloadGate = () => {
  activeCount += 1;
};

// Called by usePreventBrowserBackButton's unmount cleanup.
export const releaseReloadGate = () => {
  // Never go negative. An unbalanced release would otherwise drive the count below zero
  // and leave the gate permanently open for a later, genuinely-active flow.
  if (activeCount === 0) return;

  activeCount -= 1;
  if (activeCount > 0) return;
  if (!pendingReload) return;

  // Do not reload synchronously here. Release runs inside the guarded page's unmount
  // cleanup -- i.e. *during* the route change that exits the flow -- so reloading now
  // would discard the destination the user just chose. Wait for the next interaction on
  // the new screen. This mirrors the release shape useBuildDetector already uses.
  attachReleaseListeners();
};

// The single entry point for a gated reload. Fires immediately when no guarded flow is
// active; otherwise holds the request until one interaction after the last flow exits.
export const requestReload = (reload) => {
  if (typeof reload !== 'function') return;

  if (activeCount === 0) {
    reload();
    return;
  }

  // Last writer wins. Any held reload has the same effect as any other -- reloading the
  // document -- so there is nothing to queue.
  pendingReload = reload;
};

// Test seams. Not for production callers.
export const __getReloadGateState = () => ({
  activeCount,
  hasPendingReload: !!pendingReload,
  releaseListenersAttached,
});

export const __resetReloadGate = () => {
  detachReleaseListeners();
  activeCount = 0;
  pendingReload = null;
};
